import React from "react";
import { SectionCard, Field, Textarea, Input, inputCls } from "./ui";
import AiSuggest from "@/components/AiSuggest";
import { useProject } from "@/context/ProjectContext";
import ConstraintsContextCard from "./ConstraintsContextCard";
import InterventionDiagnosis from "./InterventionDiagnosis";
import SupportingMaterials from "./SupportingMaterials";
import { uid } from "@/lib/uid";
import { deriveSolutionPath } from "@/lib/docModel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Plus, Trash2, GraduationCap, Wrench, HelpCircle } from "lucide-react";

const BLOOM = ["Remember", "Understand", "Apply", "Analyze", "Evaluate", "Create"];
const BLOOM_VERBS = {
  Remember: "define, list, recall, name, identify, label, recognize, state",
  Understand: "explain, describe, summarize, interpret, classify, compare, paraphrase",
  Apply: "apply, demonstrate, use, implement, solve, execute, illustrate",
  Analyze: "analyze, differentiate, organize, contrast, examine, categorize",
  Evaluate: "evaluate, judge, critique, justify, assess, recommend, defend",
  Create: "create, design, develop, formulate, construct, produce, compose",
};
const MEDIA_STATUS = ["Not Started", "In Progress", "Review", "Complete"];

function BloomHelp() {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" data-testid="bloom-help-btn" className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-emerald-600">
          <HelpCircle className="h-4 w-4" /> Verb guide
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid="bloom-help-popover">
        <p className="font-display font-semibold text-slate-900 text-sm mb-1">Bloom's Taxonomy — action verbs</p>
        <p className="text-xs text-slate-500 mb-3">Start each objective with a measurable verb that matches the cognitive level.</p>
        <div className="space-y-2">
          {BLOOM.map((level) => (
            <div key={level} data-testid={`bloom-verbs-${level}`}>
              <span className="inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{level}</span>
              <p className="text-xs text-slate-600 mt-1">{BLOOM_VERBS[level]}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function NonTrainingPath({ nt, setNt }) {
  return (
    <SectionCard
      title="Non-Training Solutions"
      desc="Knowledge/Skills was not flagged as a cause in Phase 2 — training is not the answer. Select non-training interventions."
      right={<div className="flex items-center gap-2 text-amber-600 text-sm font-medium"><Wrench className="h-4 w-4" /> Non-Training Path</div>}
      testid="phase3-non-training"
    >
      <div className="space-y-4">
        <Field label="Job Aids" hint="Checklists, reference cards, decision trees that support performance in the moment.">
          <Textarea data-testid="job-aids-input" rows={3} value={nt.job_aids} onChange={(e) => setNt("job_aids", e.target.value)} />
        </Field>
        <Field label="Process Redesign" hint="Workflow, tooling and SOP changes to remove environmental barriers.">
          <Textarea data-testid="process-redesign-input" rows={3} value={nt.process_redesign} onChange={(e) => setNt("process_redesign", e.target.value)} />
        </Field>
        <Field label="Compensation / Incentive Changes" hint="Adjust rewards, recognition and consequences to align motives.">
          <Textarea data-testid="compensation-input" rows={3} value={nt.compensation_changes} onChange={(e) => setNt("compensation_changes", e.target.value)} />
        </Field>
      </div>
    </SectionCard>
  );
}

function DesignTab({ design, pd, onAi, addObjective, updObjective, delObjective, onCourseOutline, blueprint, addBlueprint, updBlueprint, delBlueprint, arcs, setArcs }) {
  return (
    <TabsContent value="design" className="pt-5 space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display font-semibold text-slate-800 flex items-center gap-2">
            Learning Objectives <span className="text-slate-400 font-normal text-sm">(Bloom's Taxonomy)</span>
            <BloomHelp />
          </h4>
          <div className="flex gap-2">
            <AiSuggest kind="objectives" context={{ goals: pd.phase1?.business_goals, gap: pd.phase1, notes: pd.phase2?.notes }} onResults={onAi} label="AI Objectives" />
            <button data-testid="add-objective-btn" onClick={addObjective} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>
          </div>
        </div>
        <div className="space-y-2">
          {design.objectives.length === 0 && <p className="text-sm text-slate-400">No objectives yet. Add manually or use AI.</p>}
          {design.objectives.map((o, i) => (
            <div key={o.id || i} data-testid={`objective-row-${i}`} className="flex gap-2 items-center">
              <select value={o.bloom_level} onChange={(e) => updObjective(i, "bloom_level", e.target.value)} className="w-32 flex-shrink-0 border border-slate-300 rounded-md px-2 py-2 text-sm bg-white outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500" data-testid={`objective-bloom-${i}`}>
                {BLOOM.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <input value={o.text} onChange={(e) => updObjective(i, "text", e.target.value)} placeholder="By the end of training, learners will be able to…" className={`${inputCls} flex-1 min-w-0`} data-testid={`objective-text-${i}`} />
              <button onClick={() => delObjective(i)} className="text-slate-300 hover:text-red-500 flex-shrink-0" data-testid={`delete-objective-${i}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
      <Field label="Course Outline">
        <Textarea data-testid="course-outline-input" rows={5} value={design.course_outline} onChange={(e) => onCourseOutline(e.target.value)} placeholder="Module 1: …&#10;Module 2: …" />
      </Field>

      <div data-testid="phase3-blueprint">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display font-semibold text-slate-800">Instructional Blueprint <span className="text-slate-400 font-normal text-sm">(per performance objective)</span></h4>
          <button data-testid="add-blueprint-btn" onClick={addBlueprint} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add Objective</button>
        </div>
        <div className="space-y-4">
          {blueprint.length === 0 && <p className="text-sm text-slate-400">No performance objectives yet.</p>}
          {blueprint.map((b, i) => (
            <div key={b.id || i} data-testid={`blueprint-row-${i}`} className="border border-slate-200 rounded-lg p-4 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs uppercase tracking-wide font-semibold text-emerald-600">Performance Objective #{i + 1}</span>
                <button onClick={() => delBlueprint(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
              <input value={b.objective} onChange={(e) => updBlueprint(i, "objective", e.target.value)} placeholder="Performance objective" className={inputCls} data-testid={`blueprint-objective-${i}`} />
              <div className="grid sm:grid-cols-2 gap-2">
                <textarea value={b.enabling} onChange={(e) => updBlueprint(i, "enabling", e.target.value)} placeholder="Enabling objectives (one per line)" rows={2} className={`${inputCls} resize-y`} data-testid={`blueprint-enabling-${i}`} />
                <textarea value={b.assessment} onChange={(e) => updBlueprint(i, "assessment", e.target.value)} placeholder="Assessment plan" rows={2} className={`${inputCls} resize-y`} />
                <textarea value={b.strategy} onChange={(e) => updBlueprint(i, "strategy", e.target.value)} placeholder="Instructional strategy" rows={2} className={`${inputCls} resize-y`} />
                <textarea value={b.delivery} onChange={(e) => updBlueprint(i, "delivery", e.target.value)} placeholder="Delivery method" rows={2} className={`${inputCls} resize-y`} />
                <textarea value={b.practice} onChange={(e) => updBlueprint(i, "practice", e.target.value)} placeholder="Practice & feedback plan" rows={2} className={`${inputCls} resize-y sm:col-span-2`} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div data-testid="phase3-arcs">
        <h4 className="font-display font-semibold text-slate-800 mb-3">Motivation Design — ARCS Model</h4>
        <div className="grid sm:grid-cols-2 gap-3">
          {[["attention", "Attention"], ["relevance", "Relevance"], ["confidence", "Confidence"], ["satisfaction", "Satisfaction"]].map(([k, label]) => (
            <div key={k}>
              <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
              <textarea data-testid={`arcs-${k}`} value={arcs[k] || ""} onChange={(e) => setArcs(k, e.target.value)} rows={2} className={`${inputCls} resize-y`} placeholder={`How the design supports ${label.toLowerCase()}…`} />
            </div>
          ))}
        </div>
      </div>
    </TabsContent>
  );
}

function DevelopmentTab({ dev, addAsset, updAsset, delAsset, addQa, updQa, delQa }) {
  return (
    <TabsContent value="development" className="pt-5 space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display font-semibold text-slate-800">Media Asset Tracker</h4>
          <button data-testid="add-asset-btn" onClick={addAsset} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add Asset</button>
        </div>
        <div className="overflow-x-auto border border-slate-200 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
              <tr><th className="text-left px-3 py-2 font-semibold">Asset</th><th className="text-left px-3 py-2 font-semibold">Type</th><th className="text-left px-3 py-2 font-semibold">Status</th><th className="text-left px-3 py-2 font-semibold">Owner</th><th></th></tr>
            </thead>
            <tbody>
              {dev.media_assets.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-slate-400">No assets tracked yet.</td></tr>}
              {dev.media_assets.map((a, i) => (
                <tr key={a.id || i} className="border-t border-slate-100" data-testid={`asset-row-${i}`}>
                  <td className="px-3 py-2"><input value={a.name} onChange={(e) => updAsset(i, "name", e.target.value)} placeholder="Asset name" className="w-full outline-none bg-transparent" data-testid={`asset-name-${i}`} /></td>
                  <td className="px-3 py-2"><input value={a.type} onChange={(e) => updAsset(i, "type", e.target.value)} className="w-24 outline-none bg-transparent" /></td>
                  <td className="px-3 py-2">
                    <select value={a.status} onChange={(e) => updAsset(i, "status", e.target.value)} className="bg-transparent outline-none text-sm" data-testid={`asset-status-${i}`}>
                      {MEDIA_STATUS.map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2"><input value={a.owner} onChange={(e) => updAsset(i, "owner", e.target.value)} placeholder="Owner" className="w-28 outline-none bg-transparent" /></td>
                  <td className="px-3 py-2"><button onClick={() => delAsset(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display font-semibold text-slate-800">QA Checklist</h4>
          <button data-testid="add-qa-btn" onClick={addQa} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add Item</button>
        </div>
        <div className="space-y-2">
          {dev.qa_checklist.length === 0 && <p className="text-sm text-slate-400">No QA items yet.</p>}
          {dev.qa_checklist.map((q, i) => (
            <div key={q.id || i} className="flex items-center gap-3" data-testid={`qa-row-${i}`}>
              <input type="checkbox" checked={!!q.done} onChange={(e) => updQa(i, "done", e.target.checked)} className="h-4 w-4 accent-emerald-500" data-testid={`qa-check-${i}`} />
              <input value={q.item} onChange={(e) => updQa(i, "item", e.target.value)} placeholder="QA item…" className={`${inputCls} ${q.done ? "line-through text-slate-400" : ""}`} />
              <button onClick={() => delQa(i)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </TabsContent>
  );
}

function SolutionGroup({ label, items, onAdd, onUpd, onDel, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-display font-semibold text-slate-800 text-sm">{label}</h4>
        <button data-testid={`add-${testid}-btn`} onClick={onAdd} className="inline-flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <p className="text-sm text-slate-400">No solutions yet.</p>}
        {items.map((s, i) => (
          <div key={s.id || i} className="grid sm:grid-cols-2 gap-2 border border-slate-200 rounded-md p-3 relative" data-testid={`${testid}-row-${i}`}>
            <input value={s.title} onChange={(e) => onUpd(i, "title", e.target.value)} placeholder="Solution title" className={inputCls} data-testid={`${testid}-title-${i}`} />
            <input value={s.need} onChange={(e) => onUpd(i, "need", e.target.value)} placeholder="Need addressed" className={inputCls} />
            <textarea value={s.description} onChange={(e) => onUpd(i, "description", e.target.value)} placeholder="Description" rows={2} className={`${inputCls} resize-y`} />
            <textarea value={s.outcomes} onChange={(e) => onUpd(i, "outcomes", e.target.value)} placeholder="Proposed outcome(s)" rows={2} className={`${inputCls} resize-y`} />
            <button onClick={() => onDel(i)} className="absolute -top-2 -right-2 bg-white border border-slate-200 rounded-full p-1 text-slate-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProposedSolutions({ solutions, mk }) {
  const learning = mk("learning");
  return (
    <SectionCard title="Recommended Solution — Training" desc="Recommended training/learning solutions mapped to the diagnosed needs (Title, Need Addressed, Description, Outcomes)." testid="phase3-solutions">
      <div className="space-y-5">
        <SolutionGroup label="Training / Learning Solution(s)" items={learning.items} onAdd={learning.add} onUpd={learning.upd} onDel={learning.del} testid="learning-solution" />
      </div>
    </SectionCard>
  );
}

export default function Phase3() {
  const { phaseData: pd, setPhase } = useProject();
  const env = pd.phase2?.environmental || {};
  const ind = pd.phase2?.individual || {};
  const hasLearning = !!ind.knowledge_skills;
  const hasNonLearning = !!(env.information || env.tools || env.incentives || ind.motives);
  const path = deriveSolutionPath(hasLearning, hasNonLearning);
  const p = pd.phase3 || {};
  const addie = p.addie || { design: { objectives: [], course_outline: "" }, development: { media_assets: [], qa_checklist: [] } };
  const nt = p.non_training || { job_aids: "", process_redesign: "", compensation_changes: "" };

  const setAddie = (next) => setPhase("phase3", { ...p, addie: next });
  const setNt = (k, v) => setPhase("phase3", { ...p, non_training: { ...nt, [k]: v } });

  // ---- Design ----
  const design = addie.design;
  const genId = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `obj-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  // Backfill stable IDs on any legacy objectives so forward-referenced scores stay bound to the objective, not its index.
  React.useEffect(() => {
    if (design.objectives.some((o) => !o.id)) {
      const fixed = design.objectives.map((o) => (o.id ? o : { ...o, id: genId() }));
      setAddie({ ...addie, design: { ...design, objectives: fixed } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [design.objectives]);

  const addObjective = () => setAddie({ ...addie, design: { ...design, objectives: [...design.objectives, { id: genId(), bloom_level: "Apply", text: "" }] } });
  const updObjective = (i, k, v) => { const o = [...design.objectives]; o[i] = { ...o[i], [k]: v }; setAddie({ ...addie, design: { ...design, objectives: o } }); };
  const delObjective = (i) => setAddie({ ...addie, design: { ...design, objectives: design.objectives.filter((_, idx) => idx !== i) } });
  const aiObjectives = (sugs) => setAddie({ ...addie, design: { ...design, objectives: [...design.objectives, ...sugs.map((s) => ({ id: genId(), bloom_level: s.bloom_level || "Apply", text: s.text || "" }))] } });
  const setCourseOutline = (v) => setAddie({ ...addie, design: { ...design, course_outline: v } });

  // ---- Instructional Blueprint + ARCS (Design Document) ----
  const blueprint = design.blueprint || [];
  const addBlueprint = () => setAddie({ ...addie, design: { ...design, blueprint: [...blueprint, { id: uid(), objective: "", enabling: "", assessment: "", strategy: "", delivery: "", practice: "" }] } });
  const updBlueprint = (i, k, v) => { const b = [...blueprint]; b[i] = { ...b[i], [k]: v }; setAddie({ ...addie, design: { ...design, blueprint: b } }); };
  const delBlueprint = (i) => setAddie({ ...addie, design: { ...design, blueprint: blueprint.filter((_, idx) => idx !== i) } });
  const arcs = design.arcs || { attention: "", relevance: "", confidence: "", satisfaction: "" };
  const setArcs = (k, v) => setAddie({ ...addie, design: { ...design, arcs: { ...arcs, [k]: v } } });

  // ---- Development ---- (defensive defaults so legacy projects with an older addie.development shape don't crash)
  const dev = { media_assets: [], qa_checklist: [], ...addie.development };
  const addAsset = () => setAddie({ ...addie, development: { ...dev, media_assets: [...dev.media_assets, { id: uid(), name: "", type: "Video", status: "Not Started", owner: "" }] } });
  const updAsset = (i, k, v) => { const a = [...dev.media_assets]; a[i] = { ...a[i], [k]: v }; setAddie({ ...addie, development: { ...dev, media_assets: a } }); };
  const delAsset = (i) => setAddie({ ...addie, development: { ...dev, media_assets: dev.media_assets.filter((_, idx) => idx !== i) } });
  const addQa = () => setAddie({ ...addie, development: { ...dev, qa_checklist: [...dev.qa_checklist, { id: uid(), item: "", done: false }] } });
  const updQa = (i, k, v) => { const q = [...dev.qa_checklist]; q[i] = { ...q[i], [k]: v }; setAddie({ ...addie, development: { ...dev, qa_checklist: q } }); };
  const delQa = (i) => setAddie({ ...addie, development: { ...dev, qa_checklist: dev.qa_checklist.filter((_, idx) => idx !== i) } });

  // ---- Proposed Solutions (shared across both paths) ----
  const solutions = p.solutions || { learning: [], non_learning: [] };
  const mkSolutions = (group) => {
    const items = solutions[group] || [];
    return {
      items,
      add: () => setPhase("phase3", { ...p, solutions: { ...solutions, [group]: [...items, { id: uid(), title: "", need: "", description: "", outcomes: "" }] } }),
      upd: (i, k, v) => { const a = [...items]; a[i] = { ...a[i], [k]: v }; setPhase("phase3", { ...p, solutions: { ...solutions, [group]: a } }); },
      del: (i) => setPhase("phase3", { ...p, solutions: { ...solutions, [group]: items.filter((_, idx) => idx !== i) } }),
    };
  };

  const PATH_INFO = {
    learning: { label: "Learning Solution", desc: "Knowledge/Skills is the confirmed cause — a training intervention is recommended.", cls: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: GraduationCap },
    non_learning: { label: "Non-Learning Solution", desc: "Causes are environmental/motivational — non-training interventions are recommended.", cls: "bg-amber-50 border-amber-200 text-amber-700", icon: Wrench },
    blend: { label: "Blended Solution (Training + Non-Training)", desc: "Both capability and environmental/motivational causes were flagged — a blend of training and non-training interventions is recommended.", cls: "bg-indigo-50 border-indigo-200 text-indigo-700", icon: GraduationCap },
  };

  const PathBanner = () => {
    const m = PATH_INFO[path];
    if (!m) return null;
    return (
      <div data-testid="phase3-path-banner" className={`flex items-start gap-3 rounded-lg border p-4 ${m.cls}`}>
        <m.icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold">Phase 2 recommends · <span data-testid="phase3-path-label">{m.label}</span></p>
          <p className="text-sm mt-0.5">{m.desc}</p>
        </div>
      </div>
    );
  };

  if (path === "none") {
    return (
      <div className="space-y-6 max-w-4xl">
        <InterventionDiagnosis />
        <SectionCard title="Recommendation Pending" testid="phase3-empty">
          <p data-testid="phase3-none-prompt" className="text-sm text-slate-500">
            No causes have been flagged yet. Complete your <span className="font-semibold">Cause Analysis in Phase 2</span> (Gilbert's BEM) to generate a recommended intervention path here.
          </p>
        </SectionCard>
        <SupportingMaterials phaseKey="phase3" />
      </div>
    );
  }

  const showTraining = path === "learning" || path === "blend";
  const showNonTraining = path === "non_learning" || path === "blend";

  return (
    <div className="space-y-6">
      <PathBanner />
      <InterventionDiagnosis />
      {showTraining && (
        <div className="flex flex-col lg:flex-row gap-6 items-start">
          <div className="flex-1 min-w-0 space-y-6 relative z-10">
            <SectionCard
              title="ADDIE Training Engine"
              desc="Knowledge/Skills is a confirmed cause — build the training solution."
              right={<div className="flex items-center gap-2 text-emerald-600 text-sm font-medium"><GraduationCap className="h-4 w-4" /> Training</div>}
              testid="phase3-addie"
            >
              <Tabs defaultValue="design" className="w-full">
                <TabsList className="bg-slate-100">
                  <TabsTrigger value="design" data-testid="addie-tab-design">Design</TabsTrigger>
                  <TabsTrigger value="development" data-testid="addie-tab-development">Development</TabsTrigger>
                </TabsList>
                <DesignTab design={design} pd={pd} onAi={aiObjectives} addObjective={addObjective} updObjective={updObjective} delObjective={delObjective} onCourseOutline={setCourseOutline} blueprint={blueprint} addBlueprint={addBlueprint} updBlueprint={updBlueprint} delBlueprint={delBlueprint} arcs={arcs} setArcs={setArcs} />
                <DevelopmentTab dev={dev} addAsset={addAsset} updAsset={updAsset} delAsset={delAsset} addQa={addQa} updQa={updQa} delQa={delQa} />
              </Tabs>
            </SectionCard>
          </div>
          <ConstraintsContextCard />
        </div>
      )}
      {showNonTraining && <NonTrainingPath nt={nt} setNt={setNt} />}
      {showTraining && <ProposedSolutions solutions={solutions} mk={mkSolutions} />}
      <SupportingMaterials phaseKey="phase3" />
    </div>
  );
}
